/* eslint-disable react-hooks/exhaustive-deps */
import React, { createRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import SearchGridWrapper from 'app/layout/pages/SearchGridWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { revalidateChipSelection } from '@AMIEWEB/GlobalSearch/helper';
import { selectFilters, selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { RootState } from 'types';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { defaultFilterValues } from './filterUtils';
import { FacilityOrderPayPackagesChips } from './FacilityOrderPayPackagesChips';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { FacilityOrderPayPackagesFilters } from './FacilityOrderPayPackagesFilters';
import { FacilityOrderPayPackagesGrid } from './FacilityOrderPayPackagesGrid';
import { Box, Typography } from '@mui/material';

export const FacilityOrderPayPackagesWrapper = ({ facilityId }: { facilityId: number }) => {
  const dispatch = useDispatch();
  const filterPreferencesId = `facility-order-pay-packages-filter-${facilityId}`;

  const formMethods = useForm({ defaultValues: defaultFilterValues });
  const { handleSubmit, reset } = formMethods;

  const gridState = useSelector(selectGridState);
  const appliedFilterValues = useSelector(selectFilters);

  const filterPreference = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === filterPreferencesId);
  });
  const { loaded: preferencesLoaded } = useSelector(selectUserPreference);


  const hiddenSubmitButton = createRef<HTMLButtonElement>();

  const onApplyFilter = (filterValues: any, newSelectedChipFilters?: any) => {
    dispatch(gridStateActions.setPageNumber(1));
    const selectedChipFilters =
      newSelectedChipFilters ||
      revalidateChipSelection(filterValues, gridState.filterAttributes?.selectedChipFilters || []);
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(filterValues ?? defaultFilterValues),
        selectedChipFilters,
      }),
    );
  };

  const onApplyFilterAndSave = (filterValues: any, newSelectedChipFilters?: any) => {
    const preferenceData = {
      id: filterPreferencesId,
      value: {
        filterValues: filterValues,
        newSelectedChipFilters: newSelectedChipFilters ?? appliedFilterValues?.selectedChipFilters,
      },
    };
    onApplyFilter(preferenceData.value.filterValues, newSelectedChipFilters);
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
  };

  const saveFilterPreferences = (filterValues: any, newSelectedChipFilters?: any) => {
    onApplyFilterAndSave(filterValues, newSelectedChipFilters);
  };

  useEffect(() => {
    if (Number(facilityId) > 0) {
      dispatch(orderDataActions.getOrderPayPackages({ facilityId: Number(facilityId) }));
    }
  }, [facilityId]);

  useEffect(() => {
    if (preferencesLoaded) {
      const payload = filterPreference?.value ?? { filterValues: defaultFilterValues };
      reset(payload?.filterValues);
      onApplyFilter(payload?.filterValues, payload?.newSelectedChipFilters);
    }
  }, [filterPreference, preferencesLoaded]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit((filter: any) => onApplyFilterAndSave(filter))}>
        <SearchGridWrapper
          filters={
            <FacilityOrderPayPackagesFilters
              facilityId={facilityId}
              saveFilterPreferences={saveFilterPreferences}
            />
          }
          table={<FacilityOrderPayPackagesGrid facilityId={facilityId} />}
          chips={<FacilityOrderPayPackagesChips onApplyFilter={onApplyFilterAndSave} />}
          tableMinHeight={430}
          loading={false}
        />
        <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
      </form>
    </FormProvider>
  );
};
